import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronRight, X } from 'lucide-react';
import { NAV_SECTIONS, isGroup, type NavGroup, type NavLeaf } from '@/config/navigation';
import { useModule } from '@/hooks/useModule';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

const SIDEBAR_MIN = 200;
const STORAGE_KEY = 'pms.sidebar.width';

function getInitialWidth(): number {
  if (typeof window === 'undefined') return 240;
  const saved = Number(localStorage.getItem(STORAGE_KEY));
  if (saved && saved >= SIDEBAR_MIN) return saved;
  return 240;
}

function LeafLink({ item, onNavigate }: { item: NavLeaf; onNavigate: () => void }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      onClick={onNavigate}
      end={item.path === '/dashboard'}
      className={({ isActive }) =>
        cn(
          'group relative flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[13px] transition-all',
          isActive
            // Glassmorphism active: frosted white tile over dark teal
            ? 'border border-white/25 bg-white/15 font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.2),0_4px_12px_-2px_rgba(0,0,0,0.2)] backdrop-blur-md'
            : 'border border-transparent text-white/75 hover:bg-white/[0.08] hover:text-white',
        )
      }
    >
      {({ isActive }) => (
        <>
          {Icon && (
            <Icon
              className={cn(
                'h-[15px] w-[15px] shrink-0',
                isActive ? 'text-white' : 'text-white/55',
              )}
            />
          )}
          <span className="truncate">{item.label}</span>
        </>
      )}
    </NavLink>
  );
}

function GroupBlock({
  group,
  pathname,
  onNavigate,
}: {
  group: NavGroup;
  pathname: string;
  onNavigate: () => void;
}) {
  const hasActiveChild = group.children.some(
    (c) => pathname === c.path || pathname.startsWith(c.path + '/'),
  );
  const [open, setOpen] = useState(hasActiveChild);

  useEffect(() => {
    if (hasActiveChild) setOpen(true);
  }, [hasActiveChild]);

  const Icon = group.icon;
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'group flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[13px] transition-colors',
          hasActiveChild
            ? 'text-white'
            : 'text-white/75 hover:bg-white/[0.08] hover:text-white',
        )}
        aria-expanded={open}
      >
        <Icon
          className={cn(
            'h-[15px] w-[15px] shrink-0',
            hasActiveChild ? 'text-white' : 'text-white/55',
          )}
        />
        <span className="flex-1 truncate text-left">{group.label}</span>
        <ChevronRight
          className={cn(
            'h-3.5 w-3.5 shrink-0 text-white/45 transition-transform',
            open && 'rotate-90',
          )}
        />
      </button>
      {open && (
        <div className="ml-3 mt-0.5 space-y-0.5 border-l border-white/15 pl-2">
          {group.children.map((child) => (
            <LeafLink key={child.path} item={child} onNavigate={onNavigate} />
          ))}
        </div>
      )}
    </div>
  );
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const modulesHook = useModule();
  const location = useLocation();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [width, setWidth] = useState<number>(getInitialWidth);
  const [resizing, setResizing] = useState(false);
  const widthRef = useRef(width);
  widthRef.current = width;

  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setResizing(true);
    const onMove = (ev: MouseEvent) => {
      const max = Math.floor(window.innerWidth * 0.25);
      const next = Math.min(Math.max(SIDEBAR_MIN, ev.clientX), max);
      setWidth(next);
    };
    const onUp = () => {
      setResizing(false);
      localStorage.setItem(STORAGE_KEY, String(widthRef.current));
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.removeProperty('cursor');
      document.body.style.removeProperty('user-select');
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const onResize = () => {
      const max = Math.floor(window.innerWidth * 0.25);
      if (widthRef.current > max) setWidth(Math.max(SIDEBAR_MIN, max));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const filteredSections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items
      .map((item) => {
        if (isGroup(item)) {
          const visibleChildren = item.children.filter(
            (c) => !c.module || modulesHook.has(c.module),
          );
          return { ...item, children: visibleChildren };
        }
        return item;
      })
      .filter((item) => {
        if (isGroup(item)) return item.children.length > 0;
        return !item.module || modulesHook.has(item.module);
      }),
  })).filter((s) => s.items.length > 0);

  const desktopStyle: CSSProperties | undefined = isDesktop ? { width: `${width}px` } : undefined;

  return (
    <>
      <div
        className={cn(
          'fixed inset-0 z-sidebar-backdrop bg-foreground/30 backdrop-blur-sm transition-opacity duration-300 ease-in-out motion-reduce:transition-none md:hidden',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={onClose}
        aria-hidden
      />

      <aside
        style={desktopStyle}
        className={cn(
          'group/sidebar fixed inset-y-0 left-0 z-sidebar flex h-screen w-[min(288px,85vw)] flex-col text-white',
          // Light-dark teal — saturated, sophisticated, NOT pitch black
          'bg-[hsl(180_35%_18%)]',
          'border-r border-white/10',
          'transition-transform duration-300 ease-in-out will-change-transform motion-reduce:transition-none',
          'md:static md:h-auto md:translate-x-0',
          open ? 'translate-x-0 shadow-2xl' : '-translate-x-full md:translate-x-0',
        )}
      >
        {/* Soft inner glow blobs for depth */}
        <div
          className="pointer-events-none absolute -left-16 top-12 h-56 w-56 rounded-full bg-teal-400/10 blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-16 left-1/4 h-44 w-44 rounded-full bg-teal-400/10 blur-3xl"
          aria-hidden
        />

        {/* Brand */}
        <div className="relative flex h-14 shrink-0 items-center justify-between border-b border-white/10 px-4">
          <div className="flex min-w-0 flex-1 items-center gap-2.5">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-white/20 bg-white/10 text-[13px] font-bold text-white backdrop-blur-sm">
              R
            </div>
            <div
              className="truncate text-[13px] font-semibold tracking-tight text-white"
              title="Raja Rani Couture Pvt Ltd"
            >
              Raja Rani Couture Pvt Ltd
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-white/60 hover:bg-white/10 hover:text-white md:hidden"
            aria-label="Close sidebar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable nav */}
        <nav className="no-scrollbar relative flex-1 overflow-y-auto overscroll-contain px-2 py-2">
          {filteredSections.map((section, sectionIdx) => (
            <div key={section.label} className={cn(sectionIdx > 0 && 'mt-4')}>
              <div className="px-2.5 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-white/40">
                {section.label}
              </div>
              <div className="space-y-0.5">
                {section.items.map((item) =>
                  isGroup(item) ? (
                    <GroupBlock
                      key={item.label}
                      group={item}
                      pathname={location.pathname}
                      onNavigate={onClose}
                    />
                  ) : (
                    <LeafLink key={item.path} item={item} onNavigate={onClose} />
                  ),
                )}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="relative shrink-0 border-t border-white/10 px-4 py-2.5">
          <div className="flex items-center gap-2 text-[10.5px] text-white/55">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.7)]" />
            <span>System online</span>
            <span className="ml-auto font-mono">v0.1</span>
          </div>
        </div>

        {/* Resize handle — desktop only */}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize sidebar"
          onMouseDown={startResize}
          className={cn(
            'absolute right-0 top-0 hidden h-full w-1 cursor-col-resize transition-colors md:block',
            'hover:bg-teal-300/40',
            resizing && 'bg-teal-300/60',
          )}
        />
      </aside>
    </>
  );
}
